<template>
    <div class="euromatch notranslate" v-bind:class="{ _live: match.isLive, _multiple: isMixParlayMode }">
        <CricketMatchInfo
            v-if="isCricket"
            v-bind:info="match"
            v-bind:marketPageType="match.marketPage"
        />
        <MatchInfo
            v-else
            v-bind:info="match"
            v-bind:marketPageType="match.marketPage"
        />
        <template v-if="!loaded">
            <div
                v-for="n in oddsColumnCount"
                v-bind:key="n"
                style=" height: 3.5rem;padding-top: 1rem;"
            >
                <Spinner />
            </div>
        </template>
        <template v-else>
            <div
                v-for="(oddsInfo, index) in oddsFormatted"
                v-bind:key="oddsInfo ? oddsInfo.oddsId : index"
                class="euromatch-mainMarket"
            >
                <template v-if="oddsInfo">
                    <Odds
                        v-for="priceOption in oddsInfo.priceOption"
                        v-bind:key="priceOption.option"
                        v-bind:isEuroSiteStyle="true"
                        v-bind:oddsInfo="oddsInfo"
                        v-bind:priceOption="priceOption"
                        v-bind:matchInfo="match"
                    />
                </template>
                <template v-else>
                    <MoreBets v-bind:info="match" />
                </template>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, toRefs } from 'vue';
    import Spinner from '@/components/common/Spinner.vue';
    import Odds from '@/components/match/Odds.vue';
    import CricketMatchInfo from '@/indiaSite/components/match/CricketMatchInfoInMainPage.vue';
    import MatchInfo from '@/indiaSite/components/match/MatchInfo.vue';
    import MoreBets from '@/indiaSite/components/match/MoreBets.vue';
    import type { IMatch } from '@/interface/IMatch';
    import type { IOdds, IPriceOption } from '@/interface/IOdds';
    import { MarketType, OddsOption, SportType } from '@/interface/enum';
    import { isSoccer } from '@/libraries/helpers/sportsHelper';
    import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
    import { getMainMarketOdds } from '@/libraries/oddsDisplay';
    import { useRootStore } from '@/stores/rootStore';
    import { EnumPresetFilterType } from '@/types';

    export default defineComponent({
        name: 'IndiaMatch',
        components: {
            Odds,
            MatchInfo,
            Spinner,
            MoreBets,
            CricketMatchInfo,
        },
        props: {
            match: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
        },
        setup(props) {
            const { match } = toRefs(props);
            const { isMixParlayMode } = storeToRefs(useRootStore());
            const isLive = computed(() => match.value.isLive);
            const presetFilter = computed(() => (isLive.value ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive));
            const marketTypes = computed(() => {
                if (isSoccer(match.value.sportType)) {
                    return [MarketType._1X2];
                }
                return [MarketType.Handicap];
            });

            const { oddsList, loaded } = useOdds({
                eventId: match.value.eventId,
                marketTypes: marketTypes.value,
                isMixParlayMode,
                presetFilter,
                isLive,
                isEnabled: true,
                sportType: match.value.sportType,
                leagueSize: match.value.leagueSize,
            });

            const oddsFormatted = computed(() => {
                const odds = getMainMarketOdds(oddsList.value, props.match.sportType, 1, true);
                const isArrayContainsNull = (arr: IOdds[] | null) => !!arr?.some(x => x === null);
                const data = isSoccer(props.match.sportType) ? odds.fullTime._1X2.data[0] : odds.fullTime.Handicap.data[0];
                if (!data || isArrayContainsNull(data)) {
                    return Array.isArray(data) ? data : [data];
                }

                const formattedData = data.map((oddsInfo, index) => {
                    if (index === 0 && oddsInfo) {
                        return {
                            ...oddsInfo,
                            priceOption: formatToEuroSiteStyle(oddsInfo.priceOption),
                        };
                    }
                    return oddsInfo;
                });

                return Array.isArray(formattedData) ? formattedData : [formattedData];
            });

            const oddsColumnCount = computed(() => (isSoccer(props.match.sportType) ? 3 : 2));

            const isCricket = computed(() => props.match.sportType === SportType.Cricket);

            function formatToEuroSiteStyle(priceOption: IPriceOption[]): IPriceOption[] {
                if (isSoccer(props.match.sportType)) {
                    priceOption = [
                        priceOption.find(x => x.oddsOption === OddsOption._1X2_1),
                        priceOption.find(x => x.oddsOption === OddsOption._1X2_X),
                        priceOption.find(x => x.oddsOption === OddsOption._1X2_2),
                    ].filter(x => x !== undefined);
                    if (priceOption.length !== 3) return [];
                }
                return priceOption;
            }

            return {
                isMixParlayMode,
                oddsFormatted,
                oddsColumnCount,
                isCricket,
                loaded,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/indiaSite/components/match/match";
</style>
